import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailRailTerminal } from "../../../../../http/hrail/types/railTerminals";
import { useHrailRailTerminalsApi } from "../../../../../http/hrail/useRailTerminals";
import { AdminItem } from "../../../../../http/types/items";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateRailTerminalModalDialogButton from "./CreateRailTerminalModalDialogButton";
import RailTerminalForm from "./RailTerminalForm";

type Props = {
  active?: boolean;
};

const RailTerminals: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "railTerminals" });
  const aborter = useRef(new AbortController());
  const railTerminalApi = useHrailRailTerminalsApi();
  const [itemToEdit, setItemToEdit] = useState<HrailRailTerminal>();
  const [itemToDelete, setItemToDelete] = useState<HrailRailTerminal>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "railTerminals");
  const isActiveTab = active ?? true;
  const updateVisibility = async (railTerminal: HrailRailTerminal, visibility: VisibilityScopeValue) => {
    try {
      await railTerminalApi.updateAdminHrailRailTerminalVisibility(railTerminal.item.id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (railTerminal: HrailRailTerminal, newOrder: number) => {
    try {
      await railTerminalApi.updateAdminHrailRailTerminalOrder(railTerminal.item.id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const { accessesAction, accessesDialog } = useEntityAccesses<AdminItem>("hrail.rail_terminal");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = railTerminalApi.useAdminHrailRailTerminals(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.railTerminals.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.railTerminals.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateRailTerminalModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailRailTerminal>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">{i18n.translation.common.terminalType}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(railTerminal) => (
          <>
            <Table.Td noWrap={true}>{railTerminal.item?.code ?? ""}</Table.Td>
            <Table.Td noWrap={false}>{formatTerminalType(i18n.translation, railTerminal.terminalType)}</Table.Td>
            <Table.Td noWrap={false}>{railTerminal.item?.description ?? ""}</Table.Td>
            <td align="center">
              <Visibility value={railTerminal.visibility} onChange={(value) => updateVisibility(railTerminal, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor
                order={railTerminal.order}
                onUpdate={(newOrder) => updateOrder(railTerminal, newOrder)}
              />
            </td>
          </>
        )}
        onRowClick={(railTerminal) => setItemToEdit(railTerminal)}
        rowActions={(railTerminal) => [
          getEditAction(i18n, () => setItemToEdit(railTerminal)),
          accessesAction(railTerminal.item),
          getDeleteAction(i18n, () => setItemToDelete(railTerminal), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.railTerminals.edit}>
          <RailTerminalForm railTerminal={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.railTerminals.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                railTerminalApi
                  .deleteAdminHrailRailTerminal(itemToDelete.item.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.railTerminals.deleteLabel,
            [itemToDelete.item?.code ?? "", itemToDelete.item?.description ?? ""].filter(Boolean).join(" - ") ||
              itemToDelete.item.id,
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

const formatTerminalType = (translation: ReturnType<typeof useI18n>["translation"], value?: string | null) => {
  switch (value) {
    case "closed":
      return translation.common.terminalTypeClosed;
    case "open":
      return translation.common.terminalTypeOpen;
    default:
      return value ?? "";
  }
};

export default RailTerminals;
